import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import api from "utils/api";
import { adminBrandParams } from "utils/brand";
import { formatPrice } from "constants/massages";
import { bookingPriceLabel } from "utils/bookingPayment";
import { useTeamAdminBrand } from "./TeamAdminBrandContext";
import { useTeamLocale } from "./TeamLocaleContext";
import {
  formatLong,
  formatShortDate,
  formatShortDateTime,
  sourceLabel,
  statusLabel,
  translateServiceLabel,
} from "./formatters";
import { useTeamAdminLiveOptional } from "./TeamAdminLiveContext";
import TeamDeleteBookingDialog from "./TeamDeleteBookingDialog";

const CUSTOMER_STAGE_OPTIONS = ["kupfer", "bronze", "silber", "gold", "unknown", "lost"];

const customerStageValue = (customer = {}) =>
  customer.customerStage === "ko" ? "lost" : customer.customerStage || "unknown";

const customerContactFormFrom = (customer = {}) => ({
  firstName: customer.firstName || "",
  lastName: customer.lastName || "",
  email: customer.email || "",
  phone: customer.phone || "",
});

const CustomersTab = () => {
  const { t, dateLocale } = useTeamLocale();
  const adminBrand = useTeamAdminBrand();
  const [customers, setCustomers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [sort, setSort] = useState("newest");
  const [pageInput, setPageInput] = useState("1");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const detailRequestRef = useRef(0);
  const pageRef = useRef(1);
  const live = useTeamAdminLiveOptional();
  const isSuperAdmin = useSelector((s) => Boolean(s.user?.isSuperAdmin));
  const [deleteCustomerTarget, setDeleteCustomerTarget] = useState(null);
  const [deletingCustomer, setDeletingCustomer] = useState(false);
  const [creditAmountEuro, setCreditAmountEuro] = useState("");
  const [creditAction, setCreditAction] = useState("add");
  const [addingCredit, setAddingCredit] = useState(false);
  const [creditMsg, setCreditMsg] = useState(null);
  const [customerForm, setCustomerForm] = useState(customerContactFormFrom());
  const [savingCustomerDetails, setSavingCustomerDetails] = useState(false);
  const [customerDetailsMsg, setCustomerDetailsMsg] = useState(null);
  const [customerNotes, setCustomerNotes] = useState("");
  const [editingCustomerNoteId, setEditingCustomerNoteId] = useState(null);
  const [editingCustomerNoteText, setEditingCustomerNoteText] = useState("");
  const [deletingCustomerNoteId, setDeletingCustomerNoteId] = useState(null);
  const [savingCustomerNotes, setSavingCustomerNotes] = useState(false);
  const [customerNotesMsg, setCustomerNotesMsg] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [statusSavingId, setStatusSavingId] = useState(null);
  const paginationLimit = pagination.limit || 20;
  const visibleFrom =
    customers.length > 0 ? (pagination.page - 1) * paginationLimit + 1 : 0;
  const visibleTo =
    customers.length > 0 ? visibleFrom + customers.length - 1 : 0;
  const customerDetailsDirty =
    detail?.customer &&
    JSON.stringify(customerForm) !==
      JSON.stringify(customerContactFormFrom(detail.customer));

  const closeDetail = useCallback(() => {
    detailRequestRef.current += 1;
    setSelectedId(null);
    setDetail(null);
    setCustomerForm(customerContactFormFrom());
    setSavingCustomerDetails(false);
    setCustomerDetailsMsg(null);
    setCreditAmountEuro("");
    setCreditAction("add");
    setCreditMsg(null);
    setCustomerNotes("");
    setEditingCustomerNoteId(null);
    setEditingCustomerNoteText("");
    setDeletingCustomerNoteId(null);
    setCustomerNotesMsg(null);
  }, []);

  useEffect(() => {
    if (!selectedId) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") closeDetail();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [closeDetail, selectedId]);

  const load = useCallback(
    async (page = 1, { silent = false } = {}) => {
      pageRef.current = page;
      if (!silent) setLoading(true);
      setError(null);
      try {
        const { data } = await api.get("/admin/customers", {
          params: {
            ...adminBrandParams({ brand: adminBrand }),
            page,
            limit: 20,
            search: search || undefined,
            sort: sort === "newest" ? undefined : sort,
          },
        });
        setCustomers(data.customers || []);
        const nextPagination = data.pagination || { page: 1, pages: 1, total: 0 };
        setPagination(nextPagination);
        setPageInput(String(nextPagination.page || 1));
      } catch (err) {
        if (!silent) {
          setError(err?.response?.data?.message || t("customers.loadError"));
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [search, sort, adminBrand, t]
  );

  useEffect(() => {
    load(1, { silent: false });
  }, [load]);

  useEffect(() => {
    if (!live?.refreshTick) return;
    load(pageRef.current, { silent: true });
  }, [live?.refreshTick, load]);

  const openDetail = async (id) => {
    const customerId = String(id);
    const requestId = ++detailRequestRef.current;
    setSelectedId(customerId);
    setDetailLoading(true);
    setDetail(null);
    setCustomerForm(customerContactFormFrom());
    setSavingCustomerDetails(false);
    setCustomerDetailsMsg(null);
    setCreditAmountEuro("");
    setCreditAction("add");
    setCreditMsg(null);
    setCustomerNotes("");
    setEditingCustomerNoteId(null);
    setEditingCustomerNoteText("");
    setDeletingCustomerNoteId(null);
    setCustomerNotesMsg(null);
    try {
      const { data } = await api.get(`/admin/customers/${customerId}`, {
        params: adminBrandParams({ brand: adminBrand }),
      });
      if (requestId !== detailRequestRef.current) return;
      setDetail(data);
      setCustomerForm(customerContactFormFrom(data.customer));
    } catch (err) {
      if (requestId !== detailRequestRef.current) return;
      setError(err?.response?.data?.message || t("customers.detailError"));
      setSelectedId(null);
    } finally {
      if (requestId === detailRequestRef.current) {
        setDetailLoading(false);
      }
    }
  };

  const submitReferralCredit = async (event) => {
    event.preventDefault();
    if (!selectedId || !creditAmountEuro.trim()) return;
    setAddingCredit(true);
    setCreditMsg(null);
    setError(null);
    try {
      const { data } = await api.post(
        `/admin/customers/${selectedId}/referral-balance`,
        { amountEuro: creditAmountEuro.trim(), action: creditAction },
        { params: adminBrandParams({ brand: adminBrand }) }
      );
      setDetail((prev) =>
        prev ? { ...prev, customer: data.customer || prev.customer } : prev
      );
      setCreditAmountEuro("");
      setCreditMsg({ kind: "success", text: data.message });
    } catch (err) {
      setCreditMsg({
        kind: "error",
        text:
          err?.response?.data?.message ||
          (creditAction === "remove"
            ? t("customers.referralDebitError")
            : t("customers.referralCreditError")),
      });
    } finally {
      setAddingCredit(false);
    }
  };

  const applyCustomerNotesUpdate = (nextCustomer) => {
    if (!nextCustomer || !selectedId) return;
    setCustomers((prev) =>
      prev.map((customer) =>
        String(customer._id) === String(selectedId)
          ? { ...customer, ...nextCustomer }
          : customer
      )
    );
    setDetail((prev) =>
      prev ? { ...prev, customer: { ...prev.customer, ...nextCustomer } } : prev
    );
  };

  const handleCustomerFormChange = (field) => (event) => {
    setCustomerDetailsMsg(null);
    setCustomerForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const submitCustomerDetails = async (event) => {
    event.preventDefault();
    if (!selectedId || !detail?.customer) return;
    setSavingCustomerDetails(true);
    setCustomerDetailsMsg(null);
    setError(null);
    try {
      const payload = {
        firstName: customerForm.firstName.trim(),
        lastName: customerForm.lastName.trim(),
        email: customerForm.email.trim(),
        phone: customerForm.phone.trim(),
      };
      const { data } = await api.patch(
        `/admin/customers/${selectedId}`,
        payload,
        { params: adminBrandParams({ brand: adminBrand }) }
      );
      if (data.customer) {
        applyCustomerNotesUpdate(data.customer);
        setCustomerForm(customerContactFormFrom(data.customer));
      }
      setCustomerDetailsMsg({
        kind: "success",
        text: data.message || t("customers.detailsSaved"),
      });
    } catch (err) {
      setCustomerDetailsMsg({
        kind: "error",
        text: err?.response?.data?.message || t("customers.detailsError"),
      });
    } finally {
      setSavingCustomerDetails(false);
    }
  };

  const submitCustomerNotes = async (event) => {
    event.preventDefault();
    if (!selectedId || !customerNotes.trim()) return;
    setSavingCustomerNotes(true);
    setCustomerNotesMsg(null);
    setError(null);
    try {
      const { data } = await api.post(
        `/admin/customers/${selectedId}/notes`,
        { adminNotes: customerNotes },
        { params: adminBrandParams({ brand: adminBrand }) }
      );
      if (data.customer) {
        applyCustomerNotesUpdate(data.customer);
        setCustomerNotes("");
      }
      setCustomerNotesMsg({ kind: "success", text: data.message || t("customers.notesSaved") });
    } catch (err) {
      setCustomerNotesMsg({
        kind: "error",
        text: err?.response?.data?.message || t("customers.notesError"),
      });
    } finally {
      setSavingCustomerNotes(false);
    }
  };

  const beginEditCustomerNote = (note) => {
    setCustomerNotesMsg(null);
    setEditingCustomerNoteId(note.id);
    setEditingCustomerNoteText(note.content || "");
  };

  const cancelEditCustomerNote = () => {
    setEditingCustomerNoteId(null);
    setEditingCustomerNoteText("");
  };

  const submitCustomerNoteEdit = async (noteId) => {
    if (!selectedId || !noteId || !editingCustomerNoteText.trim()) return;
    setSavingCustomerNotes(true);
    setCustomerNotesMsg(null);
    setError(null);
    try {
      const { data } = await api.patch(
        `/admin/customers/${selectedId}/notes/${noteId}`,
        { adminNotes: editingCustomerNoteText },
        { params: adminBrandParams({ brand: adminBrand }) }
      );
      if (data.customer) applyCustomerNotesUpdate(data.customer);
      cancelEditCustomerNote();
      setCustomerNotesMsg({ kind: "success", text: data.message || t("customers.notesSaved") });
    } catch (err) {
      setCustomerNotesMsg({
        kind: "error",
        text: err?.response?.data?.message || t("customers.notesError"),
      });
    } finally {
      setSavingCustomerNotes(false);
    }
  };

  const deleteCustomerNote = async (noteId) => {
    if (!selectedId || !noteId) return;
    setDeletingCustomerNoteId(noteId);
    setCustomerNotesMsg(null);
    setError(null);
    try {
      const { data } = await api.delete(
        `/admin/customers/${selectedId}/notes/${noteId}`,
        { params: adminBrandParams({ brand: adminBrand }) }
      );
      if (data.customer) applyCustomerNotesUpdate(data.customer);
      if (editingCustomerNoteId === noteId) cancelEditCustomerNote();
      setCustomerNotesMsg({ kind: "success", text: data.message || t("customers.notesDeleted") });
    } catch (err) {
      setCustomerNotesMsg({
        kind: "error",
        text: err?.response?.data?.message || t("customers.notesDeleteError"),
      });
    } finally {
      setDeletingCustomerNoteId(null);
    }
  };

  const updateCustomerStatus = async (customerId, status) => {
    if (!customerId) return;
    setStatusSavingId(customerId);
    setError(null);
    try {
      const { data } = await api.patch(
        `/admin/customers/${customerId}/status`,
        { status },
        { params: adminBrandParams({ brand: adminBrand }) }
      );
      const nextCustomer = data.customer;
      if (nextCustomer) {
        setCustomers((prev) =>
          prev.map((customer) =>
            String(customer._id) === String(customerId)
              ? { ...customer, ...nextCustomer }
              : customer
          )
        );
        setDetail((prev) =>
          String(prev?.customer?._id) === String(customerId)
            ? { ...prev, customer: { ...prev.customer, ...nextCustomer } }
            : prev
        );
      }
    } catch (err) {
      setError(err?.response?.data?.message || t("customers.statusUpdateError"));
    } finally {
      setStatusSavingId(null);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    setError(null);
    try {
      const response = await api.get("/admin/customers/export", {
        params: adminBrandParams({ brand: adminBrand }),
        responseType: "blob",
      });
      const disposition = response.headers["content-disposition"];
      let filename = "kunden.csv";
      const match =
        disposition && /filename="?([^";\n]+)"?/i.exec(disposition);
      if (match) filename = match[1];
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      let message = t("customers.exportError");
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          if (parsed.message) message = parsed.message;
        } catch {
          /* keep default message */
        }
      } else if (data?.message) {
        message = data.message;
      }
      setError(message);
    } finally {
      setExporting(false);
    }
  };

  const confirmDeleteCustomer = async () => {
    if (!deleteCustomerTarget) return;
    setDeletingCustomer(true);
    setError(null);
    try {
      await api.delete(`/admin/customers/${deleteCustomerTarget._id}`, {
        params: adminBrandParams({ brand: adminBrand }),
      });
      setCustomers((prev) =>
        prev.filter((c) => c._id !== deleteCustomerTarget._id)
      );
      setPagination((prev) => ({
        ...prev,
        total: Math.max(0, prev.total - 1),
      }));
      if (selectedId === deleteCustomerTarget._id) {
        closeDetail();
      }
      setDeleteCustomerTarget(null);
    } catch (err) {
      setError(err?.response?.data?.message || t("customers.deleteError"));
    } finally {
      setDeletingCustomer(false);
    }
  };

  return (
    <div className="team-customers">
      <p className="team-lead">{t("customers.lead")}</p>

      <form
        className="team-search"
        onSubmit={(e) => {
          e.preventDefault();
          setSearch(searchInput.trim());
        }}
      >
        <input
          type="search"
          placeholder={t("customers.searchPlaceholder")}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <button type="submit" className="btn-primary">
          {t("common.search")}
        </button>
        {search && (
          <button
            type="button"
            className="btn-ghost"
            onClick={() => {
              setSearchInput("");
              setSearch("");
            }}
          >
            {t("common.reset")}
          </button>
        )}
        <label className="team-search-select">
          <span>{t("customers.sortLabel")}</span>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            disabled={loading}
          >
            <option value="newest">{t("customers.sortNewest")}</option>
            <option value="oldest">{t("customers.sortOldest")}</option>
            <option value="bookings-desc">{t("customers.sortMostBookings")}</option>
          </select>
        </label>
        <button
          type="button"
          className="btn-ghost"
          disabled={exporting || loading}
          onClick={exportCsv}
        >
          {exporting ? t("customers.exporting") : t("customers.exportCsv")}
        </button>
      </form>

      {error && <div className="auth-alert is-error">{error}</div>}

      <div className="team-customers-layout">
        <div className="team-customers-list">
          <div className="team-customer-list-header">
            <div>
              <h3>{t("customers.registrationHistoryTitle")}</h3>
              <p className="team-muted">{t("customers.registrationHistoryLead")}</p>
            </div>
            <span>
              {t("customers.registrationHistoryCount", {
                from: visibleFrom,
                to: visibleTo,
                total: pagination.total,
              })}
            </span>
          </div>
          {loading ? (
            <div className="team-empty">{t("customers.loading")}</div>
          ) : customers.length === 0 ? (
            <div className="team-empty">{t("customers.empty")}</div>
          ) : (
            <ul className="team-customer-rows">
              {customers.map((c) => (
                <li key={c._id}>
                  <div
                    className={`team-customer-row ${selectedId === String(c._id) ? "is-active" : ""}`}
                  >
                    <button
                      type="button"
                      className="team-customer-row-hit"
                      onClick={() => openDetail(c._id)}
                      aria-label={`${c.firstName || ""} ${c.lastName || ""} ${t("customers.openDetails")}`.trim()}
                    />
                    <div className="team-customer-row-main">
                      <div>
                        <strong>
                          {c.firstName} {c.lastName}
                        </strong>
                        <span className="team-muted">{c.email}</span>
                        <time
                          className="team-customer-registered"
                          dateTime={c.createdAt}
                        >
                          {t("customers.registeredAt", {
                            date: formatShortDateTime(c.createdAt, dateLocale),
                          })}
                        </time>
                      </div>
                    </div>
                    <div className="team-customer-row-meta">
                      <span className="badge">
                        {sourceLabel(t, c.source, c.registrationSite)}
                      </span>
                      <span>{t("customers.bookingCount", { count: c.bookingCount })}</span>
                      <label className="team-status-select-label">
                        <span className="sr-only">{t("customers.field.status")}</span>
                        <select
                          className="team-status-select"
                          value={customerStageValue(c)}
                          disabled={statusSavingId === c._id}
                          onClick={(event) => event.stopPropagation()}
                          onChange={(event) => {
                            event.stopPropagation();
                            updateCustomerStatus(c._id, event.target.value);
                          }}
                        >
                          {CUSTOMER_STAGE_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                              {t(`customerStatus.${option}`)}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {pagination.pages > 1 && (
            <div className="team-pagination">
              <button
                type="button"
                disabled={pagination.page <= 1}
                onClick={() => load(pagination.page - 1)}
              >
                ←
              </button>
              <span>
                {pagination.page}/{pagination.pages}
              </span>
              <form
                className="team-page-jump"
                onSubmit={(e) => {
                  e.preventDefault();
                  const nextPage = Math.min(
                    pagination.pages,
                    Math.max(1, Number(pageInput) || pagination.page)
                  );
                  setPageInput(String(nextPage));
                  if (nextPage !== pagination.page) load(nextPage);
                }}
              >
                <label htmlFor="team-customers-page-jump">
                  {t("customers.pageJumpLabel")}
                </label>
                <input
                  id="team-customers-page-jump"
                  type="number"
                  min="1"
                  max={pagination.pages}
                  value={pageInput}
                  onChange={(e) => setPageInput(e.target.value)}
                />
                <button type="submit" disabled={loading}>
                  {t("customers.pageJumpSubmit")}
                </button>
              </form>
              <button
                type="button"
                disabled={pagination.page >= pagination.pages}
                onClick={() => load(pagination.page + 1)}
              >
                →
              </button>
            </div>
          )}
        </div>

        {selectedId && (
          <button
            type="button"
            className="team-customer-detail-backdrop"
            onClick={closeDetail}
            aria-label={t("common.close")}
          />
        )}

        <aside
          className={`team-customer-detail ${selectedId ? "is-open" : ""}`}
          role={selectedId ? "dialog" : "complementary"}
          aria-modal={selectedId ? "true" : undefined}
          aria-labelledby={detail ? "team-customer-detail-title" : undefined}
        >
          {!selectedId ? (
            <div className="team-empty">{t("customers.selectHint")}</div>
          ) : detailLoading ? (
            <div className="team-empty">{t("customers.detailLoading")}</div>
          ) : detail ? (
            <>
              <div className="team-detail-header">
                <h3 id="team-customer-detail-title">
                  {detail.customer.firstName} {detail.customer.lastName}
                </h3>
                <div className="team-detail-header-actions">
                  {isSuperAdmin && (
                    <button
                      type="button"
                      className="team-delete-btn team-delete-btn--permanent"
                      disabled={deletingCustomer}
                      onClick={() => setDeleteCustomerTarget(detail.customer)}
                    >
                      {t("customers.permanentDelete")}
                    </button>
                  )}
                  <button
                    type="button"
                    className="team-close"
                    onClick={closeDetail}
                    aria-label={t("common.close")}
                  >
                    ×
                  </button>
                </div>
              </div>
              <form className="team-customer-edit-form" onSubmit={submitCustomerDetails}>
                <h4>{t("customers.detailsEditTitle")}</h4>
                <div className="team-customer-edit-grid">
                  <label>
                    <span>{t("customers.field.firstName")}</span>
                    <input
                      type="text"
                      value={customerForm.firstName}
                      onChange={handleCustomerFormChange("firstName")}
                      maxLength={50}
                      disabled={savingCustomerDetails}
                    />
                  </label>
                  <label>
                    <span>{t("customers.field.lastName")}</span>
                    <input
                      type="text"
                      value={customerForm.lastName}
                      onChange={handleCustomerFormChange("lastName")}
                      maxLength={50}
                      disabled={savingCustomerDetails}
                    />
                  </label>
                  <label>
                    <span>{t("customers.field.email")}</span>
                    <input
                      type="email"
                      value={customerForm.email}
                      onChange={handleCustomerFormChange("email")}
                      maxLength={120}
                      disabled={savingCustomerDetails}
                    />
                  </label>
                  <label>
                    <span>{t("customers.field.phone")}</span>
                    <input
                      type="tel"
                      value={customerForm.phone}
                      onChange={handleCustomerFormChange("phone")}
                      maxLength={30}
                      disabled={savingCustomerDetails}
                    />
                  </label>
                </div>
                <div className="team-customer-edit-actions">
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={
                      savingCustomerDetails ||
                      !customerDetailsDirty ||
                      !customerForm.firstName.trim() ||
                      !customerForm.lastName.trim() ||
                      !customerForm.email.trim()
                    }
                  >
                    {savingCustomerDetails ? t("common.saving") : t("common.save")}
                  </button>
                </div>
                {customerDetailsMsg && (
                  <div
                    className={`auth-alert ${customerDetailsMsg.kind === "error" ? "is-error" : "is-success"}`}
                  >
                    {customerDetailsMsg.text}
                  </div>
                )}
              </form>
              <dl className="team-detail-dl">
                <dt>{t("customers.field.registered")}</dt>
                <dd>{formatShortDate(detail.customer.createdAt, dateLocale)}</dd>
                <dt>{t("customers.field.source")}</dt>
                <dd>
                  {sourceLabel(
                    t,
                    detail.customer.source,
                    detail.customer.registrationSite
                  )}
                </dd>
                <dt>{t("customers.field.status")}</dt>
                <dd>
                  <select
                    className="team-status-select"
                    value={customerStageValue(detail.customer)}
                    disabled={statusSavingId === detail.customer._id}
                    onChange={(event) =>
                      updateCustomerStatus(detail.customer._id, event.target.value)
                    }
                  >
                    {CUSTOMER_STAGE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {t(`customerStatus.${option}`)}
                      </option>
                    ))}
                  </select>
                </dd>
                <dt>{t("customers.field.freeChair")}</dt>
                <dd>
                  {detail.customer.freeChairBookingUsed
                    ? t("customers.freeChair.used")
                    : t("customers.freeChair.available")}
                </dd>
                <dt>{t("customers.field.emailVerified")}</dt>
                <dd>
                  {detail.customer.emailVerified ? t("common.yes") : t("common.no")}
                </dd>
                <dt>{t("customers.field.referralLink")}</dt>
                <dd>
                  {detail.customer.referralLink ? (
                    <a href={detail.customer.referralLink} target="_blank" rel="noreferrer">
                      {detail.customer.referralLink}
                    </a>
                  ) : (
                    "—"
                  )}
                </dd>
                <dt>{t("customers.field.referralUses")}</dt>
                <dd>{detail.customer.referralUseCount ?? 0}</dd>
                <dt>{t("customers.field.referralBalance")}</dt>
                <dd>{formatPrice(detail.customer.referralBalanceCents || 0)}</dd>
              </dl>
              <form className="team-referral-credit-form" onSubmit={submitCustomerNotes}>
                <h4>{t("customers.notesTitle")}</h4>
                <div className="team-referral-credit-row">
                  <label htmlFor="team-customer-admin-notes">
                    {t("customers.notesLabel")}
                  </label>
                  <textarea
                    id="team-customer-admin-notes"
                    className="team-customer-admin-notes"
                    value={customerNotes}
                    onChange={(e) => setCustomerNotes(e.target.value)}
                    placeholder={t("customers.notesPlaceholder")}
                    maxLength={2000}
                    disabled={savingCustomerNotes}
                  />
                </div>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={savingCustomerNotes || !customerNotes.trim()}
                >
                  {savingCustomerNotes ? t("common.saving") : t("customers.notesAdd")}
                </button>
                <div className="team-note-history">
                  {(detail.customer.adminNoteHistory || []).length ? (
                    detail.customer.adminNoteHistory.map((note) => (
                      <article className="team-note-history-item" key={note.id}>
                        <div className="team-note-history-meta">
                          <span>
                            {t("customers.notesCreatedAt", {
                              date: note.createdAt
                                ? formatShortDateTime(note.createdAt, dateLocale)
                                : "—",
                            })}
                          </span>
                          {note.updatedAt && note.updatedAt !== note.createdAt ? (
                            <span>
                              {t("customers.notesUpdatedAt", {
                                date: formatShortDateTime(note.updatedAt, dateLocale),
                              })}
                            </span>
                          ) : null}
                        </div>
                        {editingCustomerNoteId === note.id ? (
                          <>
                            <textarea
                              className="team-customer-admin-notes"
                              value={editingCustomerNoteText}
                              onChange={(event) =>
                                setEditingCustomerNoteText(event.target.value)
                              }
                              maxLength={2000}
                              disabled={savingCustomerNotes}
                            />
                            <div className="team-note-history-actions">
                              <button
                                type="button"
                                className="btn-ghost"
                                onClick={cancelEditCustomerNote}
                                disabled={savingCustomerNotes}
                              >
                                {t("common.cancel")}
                              </button>
                              <button
                                type="button"
                                className="btn-primary"
                                onClick={() => submitCustomerNoteEdit(note.id)}
                                disabled={
                                  savingCustomerNotes || !editingCustomerNoteText.trim()
                                }
                              >
                                {savingCustomerNotes
                                  ? t("common.saving")
                                  : t("common.save")}
                              </button>
                            </div>
                          </>
                        ) : (
                          <>
                            <p>{note.content}</p>
                            <div className="team-note-history-actions">
                              <button
                                type="button"
                                className="btn-ghost"
                                onClick={() => beginEditCustomerNote(note)}
                                disabled={savingCustomerNotes || deletingCustomerNoteId === note.id}
                              >
                                {t("customers.notesEdit")}
                              </button>
                              <button
                                type="button"
                                className="btn-ghost danger"
                                onClick={() => deleteCustomerNote(note.id)}
                                disabled={savingCustomerNotes || deletingCustomerNoteId === note.id}
                              >
                                {deletingCustomerNoteId === note.id
                                  ? t("customers.notesDeleting")
                                  : t("customers.notesDelete")}
                              </button>
                            </div>
                          </>
                        )}
                      </article>
                    ))
                  ) : (
                    <p className="team-muted">{t("customers.notesEmpty")}</p>
                  )}
                </div>
                {customerNotesMsg && (
                  <div
                    className={`auth-alert ${customerNotesMsg.kind === "error" ? "is-error" : "is-success"}`}
                  >
                    {customerNotesMsg.text}
                  </div>
                )}
              </form>
              <form className="team-referral-credit-form" onSubmit={submitReferralCredit}>
                <h4>{t("customers.referralCreditTitle")}</h4>
                <p className="team-muted">{t("customers.referralCreditLead")}</p>
                <div className="team-referral-credit-mode" role="group" aria-label={t("customers.referralCreditAction")}>
                  <button
                    type="button"
                    className={creditAction === "add" ? "is-active" : ""}
                    onClick={() => setCreditAction("add")}
                    disabled={addingCredit}
                  >
                    {t("customers.referralCreditModeAdd")}
                  </button>
                  <button
                    type="button"
                    className={creditAction === "remove" ? "is-active" : ""}
                    onClick={() => setCreditAction("remove")}
                    disabled={addingCredit}
                  >
                    {t("customers.referralCreditModeRemove")}
                  </button>
                </div>
                <div className="team-referral-credit-row">
                  <label htmlFor="team-referral-credit-amount">
                    {t("customers.referralCreditAmount")}
                  </label>
                  <div className="team-referral-credit-inputs">
                    <input
                      id="team-referral-credit-amount"
                      type="text"
                      inputMode="decimal"
                      placeholder="5,00"
                      value={creditAmountEuro}
                      onChange={(e) => setCreditAmountEuro(e.target.value)}
                      disabled={addingCredit}
                    />
                    <span className="team-referral-credit-suffix">€</span>
                    <button type="submit" className="btn-primary" disabled={addingCredit}>
                      {addingCredit
                        ? t("customers.referralCreditSubmitting")
                        : creditAction === "remove"
                          ? t("customers.referralDebitSubmit")
                          : t("customers.referralCreditSubmit")}
                    </button>
                  </div>
                </div>
                {creditMsg && (
                  <div
                    className={`auth-alert ${creditMsg.kind === "error" ? "is-error" : "is-success"}`}
                  >
                    {creditMsg.text}
                  </div>
                )}
              </form>
              <p className="team-readonly-note">{t("customers.bookingHistoryNote")}</p>
              <h4>{t("customers.bookingHistory")}</h4>
              {detail.bookings?.length === 0 ? (
                <p className="team-muted">{t("customers.noBookings")}</p>
              ) : (
                <ul className="team-detail-bookings">
                  {detail.bookings.map((b) => (
                    <li key={b._id}>
                      <strong>{translateServiceLabel(t, b)}</strong>
                      <span className="team-muted">{formatLong(b.start, dateLocale)}</span>
                      <span className="badge">{statusLabel(t, b.status)}</span>
                      <span className="team-muted">
                        {bookingPriceLabel(b, formatPrice, t("common.free"))}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : null}
        </aside>
      </div>

      <TeamDeleteBookingDialog
        open={Boolean(deleteCustomerTarget)}
        subject={
          deleteCustomerTarget
            ? {
                name: `${deleteCustomerTarget.firstName || ""} ${deleteCustomerTarget.lastName || ""}`.trim(),
                subtitle: deleteCustomerTarget.email,
              }
            : null
        }
        variant="customer"
        dateLocale={dateLocale}
        t={t}
        busy={deletingCustomer}
        onClose={() => !deletingCustomer && setDeleteCustomerTarget(null)}
        onConfirm={confirmDeleteCustomer}
      />
    </div>
  );
};

export default CustomersTab;
